/**
 * Cash movements + owner drawings (UPGRADE-01 R4/R10). Renders on the Cash page:
 *   - a transfer form (from → to account) gated on `cash.move`;
 *   - an owner-drawing form gated on `equity.distribute`;
 *   - a pending-confirmations queue with Confirm / Reject (`cash.move`).
 * Every movement is a maker-checker two-step: created pending, then confirmed
 * (which posts the balanced journal) or rejected.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "./Badge.js";
import { Button } from "./Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "./Form.js";
import { Table, TBody, Td, Th, THead, Tr } from "./Table.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

interface AccountOption {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly category: string;
  readonly currency: string;
  readonly isActive: boolean;
}

interface MovementView {
  readonly id: string;
  readonly kind: string;
  readonly fromAccountName: string;
  readonly toAccountName: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly status: string;
  readonly note: string | null;
  readonly createdAt: string;
}

// Accounts money can move between (treasury).
const MOVABLE = new Set(["cash_on_hand", "bank", "mobile_money", "stock"]);

export function CashMovementsPanel(): JSX.Element | null {
  const auth = useAuth();
  const perms = auth.user?.permissions ?? [];
  const canMove = perms.includes("cash.move");
  const canDraw = perms.includes("equity.distribute");
  const canRead = perms.includes("ledger.read");
  const qc = useQueryClient();

  const accountsQ = useQuery({
    queryKey: ["accounting", "accounts"],
    queryFn: () => api.get<readonly AccountOption[]>("/api/accounting/accounts"),
    enabled: canRead,
  });
  const movementsQ = useQuery({
    queryKey: ["accounting", "movements"],
    queryFn: () => api.get<readonly MovementView[]>("/api/cash-movements"),
    enabled: canRead,
  });

  const movable = useMemo(
    () => (accountsQ.data ?? []).filter((a) => a.isActive && MOVABLE.has(a.category)),
    [accountsQ.data],
  );

  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [drawFromId, setDrawFromId] = useState("");
  const [drawAmount, setDrawAmount] = useState("");
  const [drawNote, setDrawNote] = useState("");
  const [drawError, setDrawError] = useState<string | null>(null);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["accounting", "movements"] });
    void qc.invalidateQueries({ queryKey: ["accounting", "accounts"] });
  };

  const createTransfer = useMutation({
    mutationFn: (body: {
      fromAccountId: string;
      toAccountId: string;
      amountMinor: number;
      note?: string;
    }) => api.post("/api/cash-movements", body),
    onSuccess: () => {
      setFromId("");
      setToId("");
      setAmount("");
      setNote("");
      setError(null);
      invalidate();
    },
    onError: (e: unknown) => setError(apiErrorMessage(e)),
  });

  const createDrawing = useMutation({
    mutationFn: (body: { fromAccountId: string; amountMinor: number; note?: string }) =>
      api.post("/api/cash-movements/drawings", body),
    onSuccess: () => {
      setDrawFromId("");
      setDrawAmount("");
      setDrawNote("");
      setDrawError(null);
      invalidate();
    },
    onError: (e: unknown) => setDrawError(apiErrorMessage(e)),
  });

  const confirmM = useMutation({
    mutationFn: (id: string) => api.post(`/api/cash-movements/${id}/confirm`, {}),
    onSuccess: invalidate,
  });
  const rejectM = useMutation({
    mutationFn: (id: string) => api.post(`/api/cash-movements/${id}/reject`, {}),
    onSuccess: invalidate,
  });

  if (!canRead) return null;

  const movements = movementsQ.data ?? [];
  const pending = movements.filter((m) => m.status === "pending");

  function submitTransfer(): void {
    setError(null);
    const n = Number(amount);
    if (!fromId || !toId) return setError("Pick a from and a to account.");
    if (fromId === toId) return setError("From and to must differ.");
    if (!Number.isInteger(n) || n <= 0) return setError("Amount must be a positive whole number.");
    createTransfer.mutate({
      fromAccountId: fromId,
      toAccountId: toId,
      amountMinor: n,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  }

  function submitDrawing(): void {
    setDrawError(null);
    const n = Number(drawAmount);
    if (!drawFromId) return setDrawError("Pick the source account.");
    if (!Number.isInteger(n) || n <= 0)
      return setDrawError("Amount must be a positive whole number.");
    createDrawing.mutate({
      fromAccountId: drawFromId,
      amountMinor: n,
      ...(drawNote.trim() ? { note: drawNote.trim() } : {}),
    });
  }

  return (
    <div className="space-y-6">
      {canMove ? (
        <Card>
          <CardHeader>
            <CardTitle>New transfer</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="From account">
                <Select value={fromId} onChange={(e) => setFromId(e.target.value)}>
                  <option value="">Select…</option>
                  {movable.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="To account">
                <Select value={toId} onChange={(e) => setToId(e.target.value)}>
                  <option value="">Select…</option>
                  {movable.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Amount" hint="minor units">
                <TextInput
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <Field label="Note" hint="optional">
                <TextInput value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
            </div>
            <FormError message={error} />
            <Button onClick={submitTransfer} disabled={createTransfer.isPending}>
              {createTransfer.isPending ? "Creating…" : "Create transfer"}
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {canDraw ? (
        <Card>
          <CardHeader>
            <CardTitle>Owner drawing</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-xs text-slate-500">
              Cash taken out by the owner (not a business expense) — reduces equity, never profit.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="From account">
                <Select value={drawFromId} onChange={(e) => setDrawFromId(e.target.value)}>
                  <option value="">Select…</option>
                  {movable.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Amount" hint="minor units">
                <TextInput
                  inputMode="numeric"
                  value={drawAmount}
                  onChange={(e) => setDrawAmount(e.target.value)}
                />
              </Field>
              <Field label="Note" hint="optional">
                <TextInput value={drawNote} onChange={(e) => setDrawNote(e.target.value)} />
              </Field>
            </div>
            <FormError message={drawError} />
            <Button onClick={submitDrawing} disabled={createDrawing.isPending}>
              {createDrawing.isPending ? "Recording…" : "Record drawing"}
            </Button>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Pending confirmations ({pending.length})</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <THead>
              <Tr>
                <Th>From → To</Th>
                <Th>Kind</Th>
                <Th className="text-right">Amount</Th>
                <Th>Note</Th>
                <Th> </Th>
              </Tr>
            </THead>
            <TBody>
              {pending.length === 0 ? (
                <Tr>
                  <Td colSpan={5} className="text-slate-400">
                    Nothing pending.
                  </Td>
                </Tr>
              ) : (
                pending.map((m) => (
                  <Tr key={m.id}>
                    <Td>
                      {m.fromAccountName} → {m.toAccountName}
                    </Td>
                    <Td>
                      <Badge tone={m.kind === "drawing" ? "warning" : "neutral"}>{m.kind}</Badge>
                    </Td>
                    <Td className="text-right tabular-nums">
                      {formatMoney(m.amountMinor, m.currency)}
                    </Td>
                    <Td className="text-slate-500">{m.note ?? ""}</Td>
                    <Td>
                      {canMove ? (
                        <div className="flex gap-2 justify-end">
                          <Button
                            onClick={() => confirmM.mutate(m.id)}
                            disabled={confirmM.isPending}
                          >
                            Confirm
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => rejectM.mutate(m.id)}
                            disabled={rejectM.isPending}
                          >
                            Reject
                          </Button>
                        </div>
                      ) : null}
                    </Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </CardBody>
      </Card>
    </div>
  );
}
